// ./src/hooks/useProfitabilityReport.ts
import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { OP_RESULT_CODES, OpResult } from "@sdflc/api-helpers";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import {
  ProfitabilityReport,
  ProfitabilityReportService,
  ProfitabilityReportFilter,
} from "@/utils/api";
import { useApp } from "@/contexts";
import { ApiError, toApiDateRange } from "@/utils";

// Extend dayjs with UTC plugin
dayjs.extend(utc);

// ============================================
// Types
// ============================================

export interface UseProfitabilityReportOptions {
  /** Auto-load data on mount with default filters */
  autoLoad?: boolean;

  /** Initial car IDs filter */
  carIds?: string[];

  /** Initial tag IDs filter */
  tagIds?: string[];

  /** Initial date from (local date string YYYY-MM-DD) */
  dateFrom?: string | null;

  /** Initial date to (local date string YYYY-MM-DD) */
  dateTo?: string | null;
}

export interface ProfitabilityReportFilters {
  carIds: string[];
  tagIds: string[];
  dateFrom: string;
  dateTo: string;
}

export interface UseProfitabilityReportReturn {
  // Data state
  /** The report data */
  report: ProfitabilityReport | null;

  /** Whether the report is loading */
  isLoading: boolean;

  /** Whether the report has been loaded at least once */
  isLoaded: boolean;

  /** Whether the report has no data */
  isEmpty: boolean;

  // Current filters (local dates)
  /** Current filter state */
  filters: ProfitabilityReportFilters;

  // Core operations
  /** Fetch report with filters (dates should be local YYYY-MM-DD strings) */
  fetchReport: (
    filter?: Partial<ProfitabilityReportFilters>
  ) => Promise<OpResult>;

  /** Refresh report with current filters */
  refresh: () => Promise<OpResult>;

  /** Reset to initial state */
  reset: () => void;

  // Filter setters
  /** Set car IDs filter */
  setCarIds: (carIds: string[]) => void;

  /** Set tag IDs filter */
  setTagIds: (tagIds: string[]) => void;

  /** Set date range filter (local dates) */
  setDateRange: (dateFrom: string, dateTo: string) => void;

  // Error handling
  /** Last error */
  lastError: ApiError | null;

  /** Clear the error */
  clearError: () => void;

  // Helpers
  /** Get default date range (last 30 days) */
  getDefaultDateRange: () => { dateFrom: string; dateTo: string };
}

// ============================================
// Constants
// ============================================

const DEFAULT_DAYS_BACK = 30;

// ============================================
// Helper Functions
// ============================================

/**
 * Get default date range (last 30 days) as local date strings
 */
const getDefaultDateRangeStatic = (): { dateFrom: string; dateTo: string } => {
  const today = dayjs().startOf("day");
  const thirtyDaysAgo = today.subtract(DEFAULT_DAYS_BACK - 1, "day");

  return {
    dateFrom: thirtyDaysAgo.format("YYYY-MM-DD"),
    dateTo: today.format("YYYY-MM-DD"),
  };
};

// ============================================
// Hook
// ============================================

export const useProfitabilityReport = (
  options: UseProfitabilityReportOptions = {}
): UseProfitabilityReportReturn => {
  const {
    autoLoad = false,
    carIds: initialCarIds = [],
    tagIds: initialTagIds = [],
    dateFrom: initialDateFrom = null,
    dateTo: initialDateTo = null,
  } = options;

  // Get user from AppContext
  const { user } = useApp();
  const userId = user?.id ?? null;

  // Track previous user ID for change detection
  const previousUserIdRef = useRef<string | null | undefined>(undefined);

  // Get default dates if not provided
  const defaultDates = useMemo(() => getDefaultDateRangeStatic(), []);

  // Data state
  const [report, setReport] = useState<ProfitabilityReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  // Filter state (local dates)
  const [filters, setFilters] = useState<ProfitabilityReportFilters>({
    carIds: initialCarIds,
    tagIds: initialTagIds,
    dateFrom: initialDateFrom ?? defaultDates.dateFrom,
    dateTo: initialDateTo ?? defaultDates.dateTo,
  });

  // Use ref to track current filters for stable callbacks
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // Error state
  const [lastError, setLastError] = useState<ApiError | null>(null);

  // Computed values
  const isEmpty = useMemo(
    () => isLoaded && report === null,
    [isLoaded, report]
  );

  // ============================================
  // Core Operations
  // ============================================

  /**
   * Fetch report with filters.
   * Dates should be provided as local YYYY-MM-DD strings.
   */
  const fetchReport = useCallback(
    async (filter?: Partial<ProfitabilityReportFilters>): Promise<OpResult> => {
      setIsLoading(true);
      setLastError(null);

      // Use ref to get current filters (avoids dependency on filters state)
      const currentFilters = filtersRef.current;

      // Merge with current filters
      const mergedFilters: ProfitabilityReportFilters = {
        carIds:
          filter?.carIds !== undefined ? filter.carIds : currentFilters.carIds,
        tagIds:
          filter?.tagIds !== undefined ? filter.tagIds : currentFilters.tagIds,
        dateFrom:
          filter?.dateFrom !== undefined
            ? filter.dateFrom
            : currentFilters.dateFrom,
        dateTo:
          filter?.dateTo !== undefined ? filter.dateTo : currentFilters.dateTo,
      };

      // Update filter state only if changed
      const filtersChanged =
        JSON.stringify(mergedFilters.carIds) !==
        JSON.stringify(currentFilters.carIds) ||
        JSON.stringify(mergedFilters.tagIds) !==
        JSON.stringify(currentFilters.tagIds) ||
        mergedFilters.dateFrom !== currentFilters.dateFrom ||
        mergedFilters.dateTo !== currentFilters.dateTo;

      if (filtersChanged) {
        setFilters(mergedFilters);
      }

      try {
        // Convert local dates to UTC for API
        const { dateFrom, dateTo } = toApiDateRange(
          mergedFilters.dateFrom,
          mergedFilters.dateTo
        );

        // Build API filter — dateFrom and dateTo are required
        const apiFilter: ProfitabilityReportFilter = {
          dateFrom: dateFrom!,
          dateTo: dateTo!,
        };

        if (mergedFilters.carIds.length > 0) {
          apiFilter.carId = mergedFilters.carIds;
        }

        if (mergedFilters.tagIds.length > 0) {
          apiFilter.tagId = mergedFilters.tagIds;
        }

        const result = await ProfitabilityReportService.get(apiFilter);

        if (result.code === OP_RESULT_CODES.OK && result.data) {
          const reportData = Array.isArray(result.data)
            ? (result.data[0] as ProfitabilityReport)
            : (result.data as ProfitabilityReport);

          setReport(reportData || null);
          setIsLoaded(true);
          setLastError(null);
          return result;
        }

        setLastError(
          ApiError.fromResult(result, "Failed to load profitability report")
        );
        setReport(null);
        setIsLoaded(true);
        return result;
      } catch (error) {
        const result = OpResult.fromException(error);
        setLastError(
          ApiError.fromException(error, "Failed to load profitability report")
        );
        setReport(null);
        setIsLoaded(true);
        return result;
      } finally {
        setIsLoading(false);
      }
    },
    [] // No dependencies - uses refs
  );

  /**
   * Refresh report with current filters
   */
  const refresh = useCallback(async (): Promise<OpResult> => {
    return fetchReport();
  }, [fetchReport]);

  /**
   * Reset to initial state
   */
  const reset = useCallback(() => {
    const defaultRange = getDefaultDateRangeStatic();
    setReport(null);
    setFilters({
      carIds: initialCarIds,
      tagIds: initialTagIds,
      dateFrom: initialDateFrom ?? defaultRange.dateFrom,
      dateTo: initialDateTo ?? defaultRange.dateTo,
    });
    setLastError(null);
    setIsLoading(false);
    setIsLoaded(false);
  }, [initialCarIds, initialTagIds, initialDateFrom, initialDateTo]);

  // ============================================
  // Filter Setters
  // ============================================

  const setCarIds = useCallback((carIds: string[]) => {
    setFilters((prev) => ({ ...prev, carIds }));
  }, []);

  const setTagIds = useCallback((tagIds: string[]) => {
    setFilters((prev) => ({ ...prev, tagIds }));
  }, []);

  const setDateRange = useCallback((dateFrom: string, dateTo: string) => {
    setFilters((prev) => ({ ...prev, dateFrom, dateTo }));
  }, []);

  // ============================================
  // Error Handling
  // ============================================

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  // ============================================
  // Stable helper function
  // ============================================

  const getDefaultDateRange = useCallback(() => {
    return getDefaultDateRangeStatic();
  }, []);

  // ============================================
  // Auto-load and User Change Effects
  // ============================================

  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;

    // Skip on initial mount (when previousUserId is undefined)
    if (previousUserId === undefined) {
      // Initial load if autoLoad is enabled and user is logged in
      if (autoLoad && userId) {
        fetchReport();
      }
      return;
    }

    // User changed (including sign out)
    if (previousUserId !== userId) {
      // Always reset when user changes
      reset();

      // If new user is logged in and autoLoad is enabled, load their data
      if (autoLoad && userId) {
        fetchReport();
      }
    }
  }, [userId, autoLoad, fetchReport, reset]);

  // ============================================
  // Return
  // ============================================

  return useMemo(
    () => ({
      // Data state
      report,
      isLoading,
      isLoaded,
      isEmpty,

      // Current filters
      filters,

      // Core operations
      fetchReport,
      refresh,
      reset,

      // Filter setters
      setCarIds,
      setTagIds,
      setDateRange,

      // Error handling
      lastError,
      clearError,

      // Helpers
      getDefaultDateRange,
    }),
    [
      report,
      isLoading,
      isLoaded,
      isEmpty,
      filters,
      fetchReport,
      refresh,
      reset,
      setCarIds,
      setTagIds,
      setDateRange,
      lastError,
      clearError,
      getDefaultDateRange,
    ]
  );
};